import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, ClipboardList, Wallet, Bike } from "lucide-react";
import { fmt, PAYMENTS, SOURCES } from "@/lib/api";
import { usePanel } from "./PanelContext";

const Stat = ({ icon: Icon, label, value, testId, accent }) => (
  <div className={`rounded-3xl p-6 border ${accent ? "bg-[#0F1F45] text-white border-[#0F1F45]" : "bg-white"}`} data-testid={testId}>
    <div className="flex items-center justify-between">
      <p className={`text-sm font-medium ${accent ? "text-white/70" : "text-slate-500"}`}>{label}</p>
      <Icon className={`h-5 w-5 ${accent ? "text-[#FF6B0A]" : "text-slate-400"}`} />
    </div>
    <p className="mt-3 font-display text-3xl font-extrabold font-mono-num">{value}</p>
  </div>
);

const Bars = ({ title, data, labels, testId }) => {
  const max = Math.max(1, ...Object.values(data));
  return (
    <div className="rounded-3xl bg-white border p-6" data-testid={testId}>
      <h3 className="font-display font-bold">{title}</h3>
      <div className="mt-5 space-y-3">
        {Object.keys(data).length === 0 && <p className="text-sm text-slate-500">Bugün henüz veri yok.</p>}
        {Object.entries(data).map(([k, v]) => (
          <div key={k}>
            <div className="flex justify-between text-sm"><span>{labels[k] || k}</span><span className="font-semibold">{fmt(v)}</span></div>
            <div className="h-2 mt-1 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-[#FF6B0A]" style={{ width: `${(v / max) * 100}%` }} /></div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function Dashboard() {
  const { api, activeOrders } = usePanel();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)).catch(() => {}); }, [api, activeOrders.length]);
  if (!d) return <p className="text-slate-500">Yükleniyor...</p>;
  return (
    <div className="space-y-6 rise" data-testid="dashboard-page">
      <div>
        <h1 className="text-3xl font-extrabold">Bugünün Özeti</h1>
        <p className="text-slate-500 text-sm mt-1">{new Date().toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long" })}</p>
      </div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Stat icon={TrendingUp} label="Günlük Ciro" value={fmt(d.revenue)} testId="stat-revenue" accent />
        <Stat icon={ClipboardList} label="Sipariş Sayısı" value={d.order_count} testId="stat-order-count" />
        <Stat icon={Wallet} label="Bugünkü Gider" value={fmt(d.expenses)} testId="stat-expenses" />
        <Stat icon={Bike} label="Aktif Sipariş / Kurye" value={`${d.active_orders} / ${d.courier_count}`} testId="stat-active" />
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <Bars title="Sipariş Kaynağına Göre" data={d.by_source} labels={SOURCES} testId="chart-by-source" />
        <Bars title="Ödeme Yöntemine Göre" data={d.by_payment} labels={PAYMENTS} testId="chart-by-payment" />
      </div>
      <div className="rounded-3xl bg-white border p-6">
        <div className="flex items-center justify-between"><h3 className="font-display font-bold">Aktif Siparişler</h3><Link to="/panel/siparisler" className="text-sm text-[#FF6B0A] font-semibold" data-testid="dashboard-orders-link">Tümünü gör</Link></div>
        <div className="mt-4 divide-y">
          {activeOrders.slice(0, 6).map((o) => (
            <div key={o.id} className="py-3 flex items-center gap-3 text-sm">
              <span className="font-bold font-mono-num">#{o.no}</span><span className="text-slate-500">{SOURCES[o.source]}</span>
              <span className="truncate flex-1">{o.customer_name || o.table_name}</span><span className="font-semibold">{fmt(o.total)}</span>
            </div>
          ))}
          {!activeOrders.length && <p className="text-sm text-slate-500 py-3">Aktif sipariş yok.</p>}
        </div>
      </div>
    </div>
  );
}
